import React, { useState, useEffect, useRef } from "react";
import CommonDropDown from "../../../../Common/DropDown/CommonDropDown";
import MtiButton from "../../../../Buttons/MtiButton";
import {
  Ri360HotelCalibration,
  Ri360CarrierCalibration,
} from "../../../../../Model/StorageConfig";
import { EFootprint } from "../../../../../Model/PlateType";
import { ECarrier } from "../../../../../Model/ECarrier";
import CalibrationApi from "../../../../../api/CalibrationApi";
import ToolsApi from "../../../../../api/ToolsApi";
import { CartesianCoordinateHotelConfig } from "../../../../../Model/Calibration";
import { EAutoCalibrationResult } from "../../../../../Model/EAutoCalibrationResult";
import { ButtonAlternateLoading } from "../../../../../Icons";
import "./AutoCalibrationResultView.css";

export enum EMoveButtonType {
  Store,
  Retrieve,
}

export enum EMoveButtonState {
  Idle,
  Loading,
  Disabled,
}

export type MoveButtonsState = Map<EMoveButtonType, EMoveButtonState>;

const loadingIcon = <ButtonAlternateLoading></ButtonAlternateLoading>;

type Props = {
  storageCalibration: Ri360CarrierCalibration;
  carrier: ECarrier;
  isPsrBusy: boolean;
  disabled: boolean;
  isUpdatedPositionAutoCalibrationResultExist: boolean;
  autoCalibrationUpdated: boolean;
  updatedCoordinateHotelConfigs: CartesianCoordinateHotelConfig[];
};

export default function AutoCalibrationTest({
  storageCalibration,
  carrier,
  isPsrBusy,
  disabled,
  isUpdatedPositionAutoCalibrationResultExist,
  autoCalibrationUpdated,
  updatedCoordinateHotelConfigs,
}: Props) {
  const [hotels, setHotels] = useState([] as Ri360HotelCalibration[]);
  const [hotelCapacity, setHotelCapacity] = useState(1);
  const [selectedHotelIndex, setSelectedHotelIndex] = useState(0);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(0);

  const moduleIdentifier = storageCalibration.moduleIdentifier;

  const [moveButtonsState, setMoveButtonsState] = useState({
    [EMoveButtonType.Store.toString()]: EMoveButtonState.Idle,
    [EMoveButtonType.Retrieve.toString()]: EMoveButtonState.Idle,
  });
  const moveButtonTriggered = useRef(false);

  const updateHotel = (hotels: Ri360HotelCalibration[], hotelIndex: number) => {
    setSelectedHotelIndex(hotelIndex);
    setHotelCapacity(hotels[hotelIndex].capacity);
    updateSlot(0);
  };

  const updateSlot = (slotIndex: number) => {
    setSelectedSlotIndex(slotIndex);
  };

  useEffect(() => {
    if (storageCalibration) {
      setHotels(storageCalibration?.hotelCalibrations);
      updateHotel(storageCalibration?.hotelCalibrations, selectedHotelIndex);
    }
  }, [storageCalibration, selectedHotelIndex]);

  useEffect(() => {
    if (isPsrBusy) {
      let newState = { ...moveButtonsState };

      for (let key in moveButtonsState) {
        if (newState[key] !== EMoveButtonState.Loading) {
          newState[key] = EMoveButtonState.Disabled;
        }
      }
      setMoveButtonsState(newState);
    } else {
      setMoveButtonsState({
        [EMoveButtonType.Store.toString()]: EMoveButtonState.Idle,
        [EMoveButtonType.Retrieve.toString()]: EMoveButtonState.Idle,
      });
    }
  }, [isPsrBusy, moveButtonTriggered]);

  const setMoveButtonLoadingActive = (moveButtonType: EMoveButtonType) => {
    setMoveButtonsState({
      ...moveButtonsState,
      [moveButtonType.toString()]: EMoveButtonState.Loading,
    });

    moveButtonTriggered.current = !moveButtonTriggered.current;
  };

  return (
    <div>
      <div className="auto-calibration-result-selector with-fine-tunning">
        <div>
          {/* hotels */}
          <div className="auto-calibration-result-select">
            <label>Hotel</label>
            <CommonDropDown<number>
              value={selectedHotelIndex}
              id="hotel-select"
              onChange={(value) => {
                updateHotel(hotels, value);
              }}
              style={{
                width: 172,
                height: 32,
              }}
              dropDirection="up"
              dropDownDisplayValues={hotels.map((hotel, index) => ({
                display: EFootprint[hotel.footprint] + " " + hotel.number,
                value: index,
                disabled: false,
              }))}
              disabled={disabled}
            />
          </div>
          {/* slots */}
          <div className="auto-calibration-result-select">
            <label>Slot</label>
            <CommonDropDown<number>
              value={selectedSlotIndex}
              id="slot-select"
              onChange={(value) => {
                setSelectedSlotIndex(value);
              }}
              style={{
                width: 68,
                height: 32,
              }}
              dropDownStyle={{
                maxHeight: 125,
              }}
              dropDirection="up"
              dropDownDisplayValues={Array(hotelCapacity)
                .fill(0)
                .map((_, index) => ({
                  value: index,
                  display: (index + 1).toString(),
                  disabled: false,
                }))}
              disabled={disabled}
            />
          </div>
        </div>
        <div>
          <MtiButton
            variant="secondary"
            colorVariant="alternate"
            disabled={
              moveButtonsState[EMoveButtonType.Store.toString()] ===
                EMoveButtonState.Disabled ||
              moveButtonsState[EMoveButtonType.Store.toString()] ===
                EMoveButtonState.Loading ||
              disabled
            }
            onClick={() => {
              if (autoCalibrationUpdated) {
                CalibrationApi.updateStorageAutoCalibration(
                  carrier,
                  updatedCoordinateHotelConfigs
                ).then(() => {
                  ToolsApi.storePlate(
                    moduleIdentifier,
                    hotels[selectedHotelIndex].number,
                    selectedSlotIndex + 1,
                    isUpdatedPositionAutoCalibrationResultExist
                      ? EAutoCalibrationResult.UpdatedAutoCalibration
                      : EAutoCalibrationResult.CurrentAutoCalibration
                  ).then(() => {
                    setMoveButtonLoadingActive(EMoveButtonType.Store);
                  });
                });
              } else {
                ToolsApi.storePlate(
                  moduleIdentifier,
                  hotels[selectedHotelIndex].number,
                  selectedSlotIndex + 1,
                  isUpdatedPositionAutoCalibrationResultExist
                    ? EAutoCalibrationResult.UpdatedAutoCalibration
                    : EAutoCalibrationResult.CurrentAutoCalibration
                ).then(() => {
                  setMoveButtonLoadingActive(EMoveButtonType.Store);
                });
              }
            }}
            className={
              moveButtonsState[EMoveButtonType.Store.toString()] ===
              EMoveButtonState.Loading
                ? "btn-alternate-loading-animate"
                : ""
            }
            style={{
              width: 90,
            }}
          >
            {moveButtonsState[EMoveButtonType.Store.toString()] ===
              EMoveButtonState.Loading && loadingIcon}
            {moveButtonsState[EMoveButtonType.Store.toString()] !==
              EMoveButtonState.Loading && "STORE"}
          </MtiButton>
          <MtiButton
            variant="secondary"
            colorVariant="alternate"
            disabled={
              moveButtonsState[EMoveButtonType.Retrieve.toString()] ===
                EMoveButtonState.Disabled ||
              moveButtonsState[EMoveButtonType.Retrieve.toString()] ===
                EMoveButtonState.Loading ||
              disabled
            }
            onClick={() => {
              if (autoCalibrationUpdated) {
                CalibrationApi.updateStorageAutoCalibration(
                  carrier,
                  updatedCoordinateHotelConfigs
                ).then(() => {
                  ToolsApi.retrievePlate(
                    moduleIdentifier,
                    hotels[selectedHotelIndex].number,
                    selectedSlotIndex + 1,
                    isUpdatedPositionAutoCalibrationResultExist
                      ? EAutoCalibrationResult.UpdatedAutoCalibration
                      : EAutoCalibrationResult.CurrentAutoCalibration
                  ).then(() => {
                    setMoveButtonLoadingActive(EMoveButtonType.Retrieve);
                  });
                });
              } else {
                ToolsApi.retrievePlate(
                  moduleIdentifier,
                  hotels[selectedHotelIndex].number,
                  selectedSlotIndex + 1,
                  isUpdatedPositionAutoCalibrationResultExist
                    ? EAutoCalibrationResult.UpdatedAutoCalibration
                    : EAutoCalibrationResult.CurrentAutoCalibration
                ).then(() => {
                  setMoveButtonLoadingActive(EMoveButtonType.Retrieve);
                });
              }
            }}
            className={
              moveButtonsState[EMoveButtonType.Retrieve.toString()] ===
              EMoveButtonState.Loading
                ? "btn-alternate-loading-animate"
                : ""
            }
            style={{
              width: 116,
            }}
          >
            {moveButtonsState[EMoveButtonType.Retrieve.toString()] ===
              EMoveButtonState.Loading && loadingIcon}
            {moveButtonsState[EMoveButtonType.Retrieve.toString()] !==
              EMoveButtonState.Loading && "RETRIEVE"}
          </MtiButton>
        </div>
      </div>
    </div>
  );
}
